/** App credentials — edit the display name and avatar, manage redirect URIs, delete. */

import {
  ApiError,
  deleteApplication,
  getApplication,
  updateApplication,
  type DeveloperApp,
} from '../api.ts';
import { route } from '../config.ts';
import {
  clear,
  confirmDialog,
  el,
  errorState,
  field,
  fieldActions,
  icon,
  toast,
} from '../ui.ts';
import { UploadError, discardUpload, uploadAvatar } from '../upload.ts';
import { readPrefill } from './apps.ts';

const SECRET_MASK = '••••••••••••••••••••••••';

function copyButton(getValue: () => string, label: string): HTMLButtonElement {
  const button = el('button', {
    class: 'field-action',
    type: 'button',
    'aria-label': `Copy ${label}`,
  }, [icon('copy', 14), el('span', { text: 'Copy' })]);

  button.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(getValue());
      button.classList.add('is-done');
      const text = button.querySelector('span');
      if (text) text.textContent = 'Copied';
      window.setTimeout(() => {
        button.classList.remove('is-done');
        if (text) text.textContent = 'Copy';
      }, 1600);
    } catch {
      toast('Could not copy to the clipboard.', 'error');
    }
  });

  return button;
}

function redirectRow(value: string, onRemove: (row: HTMLElement) => void): HTMLElement {
  const input = el('input', {
    class: 'field',
    type: 'url',
    placeholder: 'https://example.com/oauth/callback',
    spellcheck: 'false',
    'aria-label': 'Redirect URI',
  });
  input.value = value;

  const remove = el('button', {
    class: 'field-action is-danger',
    type: 'button',
    'aria-label': 'Remove this redirect URI',
  }, [icon('trash', 14), el('span', { text: 'Remove' })]);

  const row = el('div', { class: 'uri-row' }, [input, remove]);
  remove.addEventListener('click', () => onRemove(row));
  return row;
}

export async function mountApp(container: HTMLElement): Promise<void> {
  const saveButton = document.querySelector<HTMLButtonElement>('[data-save-app]');
  const deleteButton = document.querySelector<HTMLButtonElement>('[data-delete-app]');

  const appId = new URLSearchParams(window.location.search).get('id');
  if (!appId) {
    clear(container);
    container.append(
      el('div', { class: 'console-empty' }, [
        el('p', { class: 'console-empty-title', text: 'No app selected' }),
        el('p', { class: 'console-empty-note', text: 'Pick an app from the list to manage it.' }),
        el('a', { class: 'btn btn-primary', href: route('apps'), text: 'Back to my apps' }),
      ]),
    );
    return;
  }

  // The list page stashes the row it was showing, so the form paints immediately and the
  // fetch below only fills in the secret and redirect URIs.
  const cached = readPrefill(appId);
  if (cached) render(cached);

  let fresh: DeveloperApp | null = null;
  try {
    fresh = await getApplication(appId);
  } catch (cause) {
    if (!cached) {
      clear(container);
      container.append(
        errorState(
          cause instanceof ApiError ? cause.message : 'Could not load this app.',
          () => window.location.reload(),
        ),
      );
      return;
    }
    toast('Showing the details we already had — refreshing failed.', 'error');
  }

  if (fresh) render(fresh);

  function render(data: DeveloperApp): void {
    clear(container);

    const name = field({
      label: 'App display name',
      id: 'app-name',
      value: data.name ?? '',
      hint: 'Shown to users on the OAuth consent screen.',
    });

    const clientId = field({
      label: 'Client ID',
      id: 'app-client-id',
      value: data.clientID ?? '',
      readonly: true,
    });
    fieldActions(clientId.wrap, [copyButton(() => data.clientID ?? '', 'client ID')]);

    const secret = field({
      label: 'Client secret',
      id: 'app-client-secret',
      value: data.clientSecret ? SECRET_MASK : '—',
      readonly: true,
      hint: 'Treat this like a password. Anyone holding it can act as your app.',
    });
    secret.input.classList.add('is-secret');

    let revealed = false;
    const reveal = el('button', { class: 'field-action', type: 'button', text: 'Show' });
    reveal.addEventListener('click', () => {
      revealed = !revealed;
      secret.input.value = revealed ? data.clientSecret ?? '—' : SECRET_MASK;
      reveal.textContent = revealed ? 'Hide' : 'Show';
    });
    if (data.clientSecret) {
      fieldActions(secret.wrap, [
        reveal,
        copyButton(() => data.clientSecret ?? '', 'client secret'),
      ]);
    }

    /* ------------------------------------------------------------- redirect URIs */

    const uriList = el('div', { class: 'uri-list' });
    const removeRow = (row: HTMLElement) => {
      row.remove();
      if (!uriList.children.length) uriList.append(emptyUris());
      uriList.dispatchEvent(new Event('input'));
    };
    const emptyUris = () =>
      el('p', { class: 'field-hint is-empty', text: 'No redirect URIs yet.' });

    const uris = data.redirectUris?.filter(Boolean) ?? [];
    if (uris.length) uris.forEach((uri) => uriList.append(redirectRow(uri, removeRow)));
    else uriList.append(emptyUris());

    const addUri = el('button', { class: 'btn btn-ghost', type: 'button' }, [
      icon('plus', 14),
      el('span', { text: 'Add redirect URI' }),
    ]);
    addUri.addEventListener('click', () => {
      uriList.querySelector('.is-empty')?.remove();
      const row = redirectRow('', removeRow);
      uriList.append(row);
      row.querySelector('input')?.focus();
      uriList.dispatchEvent(new Event('input'));
    });

    const uriSection = el('section', { class: 'console-section' }, [
      el('h2', { class: 'console-section-title', text: 'OAuth redirect URIs' }),
      el('p', {
        class: 'console-section-note',
        text:
          'A domain covers its subdomains. localhost and 127.0.0.1 are always allowed for ' +
          'local development.',
      }),
      uriList,
      addUri,
    ]);

    /* -------------------------------------------------------------------- avatar */

    // What the API currently holds. The avatar writes as soon as it changes, so it needs
    // the *saved* name and URIs to send alongside — using the live inputs would quietly
    // persist half-finished edits to the other fields.
    let savedName = data.name ?? '';
    let savedUris = data.redirectUris?.filter(Boolean) ?? [];
    let avatarUrl = data.avatar ?? '';
    // Only set for an image uploaded during this visit. Replacing or removing one deletes
    // it again; an avatar saved in an earlier session has no id here, so it is left alone.
    let pendingImageId = '';

    const preview = el('img', {
      class: 'avatar-preview',
      src: avatarUrl,
      alt: 'App avatar preview',
    });
    const noAvatar = el('span', { class: 'avatar-empty', text: 'No avatar yet' });

    const showPreview = (hasImage: boolean) => {
      preview.hidden = !hasImage;
      noAvatar.hidden = hasImage;
    };
    showPreview(Boolean(avatarUrl));
    preview.addEventListener('error', () => showPreview(false));

    const picker = el('input', {
      class: 'sr-only',
      type: 'file',
      accept: 'image/png,image/jpeg,image/webp,image/gif',
      id: 'app-avatar-file',
    });

    const uploadButton = el('button', { class: 'btn btn-ghost', type: 'button' }, [
      icon('upload', 14),
      el('span', { text: avatarUrl ? 'Replace image' : 'Upload image' }),
    ]);
    const removeButton = el('button', {
      class: 'btn btn-ghost',
      type: 'button',
      text: 'Remove',
    });
    removeButton.hidden = !avatarUrl;

    const progress = el('div', { class: 'upload-progress', hidden: true }, [
      el('div', { class: 'upload-bar' }),
    ]);
    const progressBar = progress.firstElementChild as HTMLElement;
    const uploadNote = el('p', { class: 'field-hint', text: 'PNG, JPEG, WebP or GIF, up to 5MB. Cropped to a square.' });

    const dropzone = el('div', { class: 'avatar-frame is-dropzone', tabindex: '0', role: 'button' }, [
      preview,
      noAvatar,
      el('span', { class: 'avatar-drop-hint', text: 'Drop image' }),
    ]);

    /**
     * Writes just the avatar. The image is already committed server-side by the time this
     * runs, so leaving it dependent on a separate Save press only invites losing it.
     */
    const persistAvatar = async (url: string): Promise<boolean> => {
      try {
        await updateApplication(data.id, {
          name: savedName,
          avatar: url,
          redirectUris: savedUris,
        });
        return true;
      } catch (cause) {
        toast(cause instanceof ApiError ? cause.message : 'Could not save the avatar.', 'error');
        return false;
      }
    };

    const setAvatar = (url: string, imageId = '') => {
      if (pendingImageId && pendingImageId !== imageId) void discardUpload(pendingImageId);
      pendingImageId = imageId;
      avatarUrl = url;
      preview.src = url;
      showPreview(Boolean(url));
      removeButton.hidden = !url;
      const label = uploadButton.querySelector('span');
      if (label) label.textContent = url ? 'Replace image' : 'Upload image';
    };

    const runUpload = async (file: File) => {
      uploadNote.classList.remove('is-error');
      uploadButton.disabled = true;
      dropzone.classList.add('is-busy');
      progress.hidden = false;
      progressBar.style.width = '0%';

      try {
        const uploaded = await uploadAvatar(file, (fraction) => {
          progressBar.style.width = `${Math.round(fraction * 100)}%`;
        });

        if (!(await persistAvatar(uploaded.url))) {
          // Nothing points at it now, so do not leave it behind in storage.
          void discardUpload(uploaded.imageId);
          return;
        }

        setAvatar(uploaded.url, uploaded.imageId);
        toast('Avatar updated.');
      } catch (cause) {
        const message =
          cause instanceof UploadError || cause instanceof ApiError
            ? cause.message
            : 'The upload failed.';
        uploadNote.textContent = message;
        uploadNote.classList.add('is-error');
        toast(message, 'error');
      } finally {
        uploadButton.disabled = false;
        dropzone.classList.remove('is-busy');
        progress.hidden = true;
        picker.value = '';
      }
    };

    picker.addEventListener('change', () => {
      const file = picker.files?.[0];
      if (file) void runUpload(file);
    });
    uploadButton.addEventListener('click', () => picker.click());
    dropzone.addEventListener('click', () => picker.click());
    dropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        picker.click();
      }
    });

    for (const name of ['dragenter', 'dragover'] as const) {
      dropzone.addEventListener(name, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-over');
      });
    }
    for (const name of ['dragleave', 'dragend'] as const) {
      dropzone.addEventListener(name, () => dropzone.classList.remove('is-over'));
    }
    dropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      dropzone.classList.remove('is-over');
      const file = event.dataTransfer?.files?.[0];
      if (file) void runUpload(file);
    });

    removeButton.addEventListener('click', async () => {
      removeButton.disabled = true;
      const removed = await persistAvatar('');
      removeButton.disabled = false;
      if (!removed) return;
      setAvatar('');
      toast('Avatar removed.');
    });

    const aside = el('aside', { class: 'console-aside' }, [
      el('h2', { class: 'console-section-title', text: 'App avatar' }),
      el('p', {
        class: 'console-section-note',
        text: 'Shown beside your app name on the OAuth consent screen. Saved as soon as you upload.',
      }),
      dropzone,
      progress,
      el('div', { class: 'avatar-actions' }, [uploadButton, removeButton]),
      uploadNote,
      picker,
    ]);

    /* -------------------------------------------------------------------- layout */

    container.append(
      el('div', { class: 'console-columns' }, [
        el('div', { class: 'console-column' }, [
          el('div', { class: 'field-stack' }, [name.wrap, clientId.wrap, secret.wrap]),
          uriSection,
        ]),
        aside,
      ]),
    );

    /* ------------------------------------------------------------------- actions */

    const collectUris = () =>
      [...uriList.querySelectorAll<HTMLInputElement>('input')]
        .map((input) => input.value.trim())
        .filter(Boolean);

    const sameUris = (a: string[], b: string[]) =>
      a.length === b.length && a.every((value, index) => value === b[index]);

    /*
     * Save covers the name and the redirect URIs only — the avatar writes itself the moment
     * it is uploaded or removed, so it can never be a pending change.
     */
    const isDirty = () =>
      name.input.value.trim() !== savedName || !sameUris(collectUris(), savedUris);

    const refreshDirty = () => {
      if (saveButton) saveButton.disabled = !isDirty();
    };

    name.input.addEventListener('input', refreshDirty);
    uriList.addEventListener('input', refreshDirty);

    const save = async () => {
      const value = name.input.value.trim();
      if (!value) {
        name.input.focus();
        name.wrap.classList.add('has-error');
        toast('The app needs a name.', 'error');
        return;
      }
      name.wrap.classList.remove('has-error');

      if (!saveButton) return;
      saveButton.disabled = true;
      const label = saveButton.textContent;
      saveButton.textContent = 'Saving…';

      try {
        const uris = collectUris();
        // The response is `{ success: true }`, not the app — track what was sent instead.
        await updateApplication(data.id, { name: value, avatar: avatarUrl, redirectUris: uris });

        savedName = value;
        savedUris = uris;
        data.name = value;
        data.avatar = avatarUrl;
        data.redirectUris = uris;
        // Saved, so it is no longer a throwaway upload to clean up.
        pendingImageId = '';
        toast('Changes saved.');
      } catch (cause) {
        toast(cause instanceof ApiError ? cause.message : 'Could not save your changes.', 'error');
      } finally {
        saveButton.textContent = label ?? 'Save changes';
        refreshDirty();
      }
    };

    const remove = () => {
      confirmDialog({
        title: 'Delete this app?',
        message:
          `“${data.name || 'This app'}” will stop working immediately and its client ID and ` +
          'secret are revoked. This cannot be undone.',
        confirmLabel: 'Delete app',
        onConfirm: async () => {
          try {
            await deleteApplication(data.id);
            window.location.href = route('apps');
          } catch (cause) {
            toast(cause instanceof ApiError ? cause.message : 'Could not delete the app.', 'error');
            return false;
          }
        },
      });
    };

    if (saveButton) {
      saveButton.onclick = () => void save();
      refreshDirty();
    }
    if (deleteButton) {
      deleteButton.disabled = false;
      deleteButton.onclick = remove;
    }

    document.title = `${data.name || 'App credentials'} — Livepush API`;
  }
}
